import { createContext, useContext, useState, useCallback, useEffect, useMemo, startTransition } from "react";
import { translations, loadLangPack, langFromPath, localizedPath, setCurrentLang, setPublicSiteLangs, publicSiteLangs, tr, LANG_CODES, CORE_LANGS } from "@/lib/translations";
import { api } from "@/lib/api";
import { applyAlternates, setSiteOrigin, redirectToSiteOrigin } from "@/lib/useSeo";
import { applyTextOverrides } from "@/lib/uiTexts";
import { setPortalTextOverrides } from "@/lib/portalOverrides";

const LanguageContext = createContext(null);
const CACHE_KEY = "ph_site";
const cached = () => { try { return JSON.parse(localStorage.getItem(CACHE_KEY) || "null") || {}; } catch { return {}; } };
// Design/Texte/Logo/Titel- und Inselbilder zwischenspeichern, damit die Seite ohne Flackern (Musterbild) im gespeicherten Design startet
const cacheable = (s) => Object.fromEntries(Object.entries(s).filter(([k]) => k === "ui_texts" || k === "portal_texts" || k === "site_logo_url" || k === "favicon_url" || k.startsWith("design_") || k.startsWith("home_hero_") || k.startsWith("island_image_")));

export function LanguageProvider({ children }) {
  const [lang] = useState(() => { const l = langFromPath(); setCurrentLang(l); localStorage.setItem("islasol_lang", l); return l; });
  const [packReady, setPackReady] = useState(() => !!translations[lang]);
  useEffect(() => { if (!packReady) loadLangPack(lang).then(() => setPackReady(true)); }, [lang, packReady]);
  const [, setTick] = useState(0);
  const [allSiteLangs, setAllSiteLangs] = useState(() => [...CORE_LANGS]);
  const [site, setSite] = useState(cached);
  const [siteLoaded, setSiteLoaded] = useState(false);
  // Erster Aufruf nutzt die bereits in index.html gestartete Anfrage (window.__phSettings), danach normal per API
  const fetchSite = () => {
    const early = window.__phSettings;
    window.__phSettings = null;
    return early ? early.then((d) => (d ? { data: d } : api.get("/settings"))) : api.get("/settings");
  };
  const loadSite = useCallback(() => fetchSite().then((r) => {
    setSiteOrigin(r.data.site_url);
    if (redirectToSiteOrigin()) return;
    // Als Transition: React rendert die große Seite in kleinen Häppchen statt in einem langen Block (weniger Blockierzeit auf Handys)
    startTransition(() => { setSite(r.data); setSiteLoaded(true); }); applyAlternates();
    try { localStorage.setItem(CACHE_KEY, JSON.stringify(cacheable(r.data))); } catch {}
  }).catch(() => {}), []);
  useEffect(() => {
    api.get("/languages").then((r) => {
      setPublicSiteLangs(r.data.filter((l) => l.site && l.site_public !== false).map((l) => l.code));
      setAllSiteLangs(LANG_CODES.filter((c) => r.data.some((l) => l.code === c && l.site)));
      applyAlternates(); startTransition(() => setTick((n) => n + 1));
    }).catch(() => {});
    loadSite();
  }, [loadSite]);

  const changeLang = useCallback((l) => {
    if (l === lang) return;
    localStorage.setItem("islasol_lang", l);
    window.location.assign(localizedPath(window.location.pathname, l) + window.location.search);
  }, [lang]);

  const t = useMemo(() => applyTextOverrides(translations[lang] || translations.de, site.ui_texts, lang), [lang, site.ui_texts, packReady]);
  useEffect(() => { setPortalTextOverrides(site.portal_texts); }, [site.portal_texts]);
  // Sprachpaket (ein kleiner Chunk) abwarten, statt kurz deutsche Texte zu zeigen
  if (!packReady) return null;

  return (
    <LanguageContext.Provider value={{ lang, setLang: changeLang, t, tr: (obj) => tr(obj, lang), siteLangs: allSiteLangs, publicLangs: publicSiteLangs, site, siteLoaded, reloadSite: loadSite }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLang() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error("useLang must be inside LanguageProvider");
  return ctx;
}

// Alle Website-Sprachen (DE/EN/ES + NL/PL/FR/IT, auch noch nicht öffentliche) – Basis für alle Admin-Sprachfelder; Umschalter/hreflang nutzen publicSiteLangs
export const useSiteLangs = () => useLang().siteLangs;
// Öffentliche Einstellungen (/api/settings): Design, Texte, Logo, Impressum …
export const useSiteSettings = () => useLang().site;
